import { useLayoutEffect, useRef, useState } from "react";

import { Tag, Typography } from "antd";
import { clsx } from "clsx";
import { useUpdateNodeInternals } from "reactflow";

import { MAX_NODE_TRANSITIONS, type DynamicHandle } from "Components/elements/RVFlow/constants";
import { Column, Row } from "Components/shared/Flexbox";

import { BaseFlowNodeContainer } from "../Shared/BaseFlowNodeContainer/BaseFlowNodeContainer";
import { TopHandle } from "../Shared/FlowNodeHandles";
import { BottomHandle, LeftHandle } from "../Shared/FlowNodeHandles/MultiBottomHandle";
import { splitId } from "./utils";

import styles from "../Shared/FlowNodes.module.scss";

const { Text } = Typography;

type NodeProps = {
    id: string;
    isConnectable: boolean;
    isSelected: boolean;
    handles: Array<(DynamicHandle & { noEllipsis?: boolean }) | undefined>;
    children: JSX.Element;
    connectingNodesFrom: string | null;
    removeNodeIcon: JSX.Element | null;
    minimumVisibleHandles?: number;
    isHorizontal?: boolean;
    className?: string;
};

const DEFAULT_MINIMUM_VISIBLE_HANDLES = 1;

const MAXIMUM_HANDLES = MAX_NODE_TRANSITIONS;
const HANDLE_CONTAINER_EXTRA_WIDTH = 50;
const HANDLE_CONTAINER_MIN_WIDTH = 216;

export function addEmptyHandlers(handles: any[] | undefined[], maxLength: number) {
    while (handles.length < maxLength) {
        handles.push(undefined);
    }
    return handles;
}

export function DynamicMultiBranchNode({
    id,
    isSelected,
    isConnectable,
    handles,
    children,
    connectingNodesFrom,
    removeNodeIcon,
    minimumVisibleHandles,
    isHorizontal,
    className,
}: NodeProps) {
    const [width, setWidth] = useState(0);
    const handlesContainer = useRef<any>(null);
    const slicedHandles = handles.slice(0, MAXIMUM_HANDLES);
    const preFilledHandles = addEmptyHandlers(slicedHandles, 3);
    const updateNodeInternals = useUpdateNodeInternals();

    useLayoutEffect(() => {
        if (handlesContainer?.current) {
            setWidth(handlesContainer?.current?.offsetWidth + HANDLE_CONTAINER_EXTRA_WIDTH);
        }
    }, [preFilledHandles]);

    return (
        <BaseFlowNodeContainer selected={isSelected} connectingNodesFrom={!!connectingNodesFrom} className={className}>
            <>
                {isHorizontal ? (
                    <LeftHandle isConnectable={isConnectable} />
                ) : (
                    <TopHandle isConnectable={isConnectable} />
                )}
                {removeNodeIcon}
                {/* //this should happening at the node implementation level */}
                <div style={{ width: `${width < HANDLE_CONTAINER_MIN_WIDTH ? HANDLE_CONTAINER_MIN_WIDTH : width}px` }}>
                    {children}
                </div>
                <Row gap={3} className={styles.multiHandleContainer} elementRef={handlesContainer}>
                    {preFilledHandles.map((handle, handleIndex) => {
                        // render non-breaking white space so that the empty cell with be rendered with height
                        const displayValue = handle?.displayValue || "\u00A0";
                        const handleId = splitId(id, handle?.id);
                        updateNodeInternals(id);
                        return (
                            <Column
                                key={handleId}
                                alignItems="center"
                                className={clsx(styles.handleContentText, {
                                    [styles.hiddenOption]:
                                        !handle?.connectorShouldDisplay &&
                                        handleIndex >= (minimumVisibleHandles ?? DEFAULT_MINIMUM_VISIBLE_HANDLES),
                                })}
                            >
                                <Tag className={styles.text}>
                                    <Text
                                        ellipsis
                                        className={clsx({
                                            [styles.withEllipsis]: !handle?.noEllipsis,
                                        })}
                                    >
                                        {displayValue}
                                    </Text>
                                </Tag>
                                <BottomHandle isConnectable={isConnectable} id={handleId} />
                            </Column>
                        );
                    })}
                </Row>
            </>
        </BaseFlowNodeContainer>
    );
}
